import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts } from "@/lib/api";
import MotifCircle from "@/components/site/MotifCircle";
import { MOTIF_LIST, Motif } from "@/lib/motifs";
import { Butterfly } from "@/components/site/LogoMark";
import CollectionHerbarium from "@/components/site/CollectionHerbarium";
import TwinningBand from "@/components/site/TwinningBand";
import GrownupsBand from "@/components/site/GrownupsBand";
import SeoHead from "@/components/site/SeoHead";

/**
 * Home — Option 1 (Editorial Colonnade).
 * Cream-vignetted lifestyle hero. Ink brand headline. Quiet featured card.
 * Hotspot bubbles intentionally removed per founder direction.
 * Also live at /home-option-1 for A/B viewing.
 */
const Home = () => {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProducts({ category: "girls" }).then((all) => setFeatured(all.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SeoHead
        title="Small clothes, made slowly."
        description="Hand-dyed, hand-embroidered pieces for children — and grown-ups who want to twin. Made slowly in Bengaluru by MELAVI."
        path="/"
        noSuffix
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MELAVI",
          url: "https://melavi.in",
          logo: "https://melavi.in/logo.png",
          description: "Slow-fashion children's and family clothing brand from Bengaluru, India.",
          sameAs: [],
        }}
      />
      {/* HERO — full-bleed lifestyle: girl in Butterfly Bliss Pink in the Colonnade */}
      <section className="relative w-full overflow-hidden" data-testid="hero" style={{ minHeight: "100vh" }}>
        <div className="absolute inset-0">
          <img
            src="/api/uploads/_lifestyle/girls/g-3.webp"
            alt="Butterfly Bliss Pink on a small girl in the Colonnade"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 68%" }}
          />
          {/* editorial cream vignette from the left */}
          <div className="absolute inset-0" style={{
            background: "linear-gradient(90deg, rgba(246,241,233,0.94) 0%, rgba(246,241,233,0.65) 22%, rgba(246,241,233,0.05) 45%, rgba(246,241,233,0) 100%)"
          }} />
          <div className="absolute inset-x-0 bottom-0 h-40" style={{ background: "linear-gradient(180deg, rgba(246,241,233,0) 0%, rgba(246,241,233,0.9) 100%)" }} />
        </div>
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24 min-h-[100vh] flex flex-col justify-between">
          <div className="max-w-[520px] animate-melavi-fade-up">
            <div className="nav-item mb-6 flex items-center gap-3 text-ink/70">
              <span className="w-6 h-[1px] bg-ink/50" />
              <span>Chapter One · The Colonnade</span>
            </div>

            <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-ink" data-testid="hero-headline">
              Small clothes,<br />made <em className="italic">slowly</em>.
            </h1>

            <div className="mt-12 max-w-[380px]" data-testid="hero-featured-card">
              <div className="flex items-center gap-3 mb-4">
                <Motif name="butterfly" size={22} strokeColor="var(--accent)" />
                <span className="nav-item text-ink/70">On the plinth today</span>
              </div>
              <div className="serif text-3xl md:text-4xl leading-tight">Butterfly Bliss Pink</div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link to="/product/butterfly-bliss-pink" className="btn-solid" data-testid="hero-shop-featured">See the piece</Link>
                <Link to="/shop" className="crimson-link nav-item !text-[12px]" data-testid="hero-shop-all">Enter the shop →</Link>
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-end justify-between mt-16 text-xs text-ink/70">
            <span className="flex items-center gap-3">
              <span className="inline-block w-8 h-8 rounded-full hairline flex items-center justify-center animate-melavi-breathe">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M4 12 a 8 6 0 0 0 16 0 a 8 6 0 0 0 -16 0" />
                  <path d="M4 12 L 2 10 M4 12 L 2 14" strokeLinecap="round" />
                </svg>
              </span>
              360° · drag to turn
            </span>
            <span className="max-w-[36ch] leading-relaxed text-right">
              Carved honey sandstone. Dust in golden light.
            </span>
          </div>
        </div>
      </section>

      {/* SHOP BY MOTIF */}
      <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-32" data-testid="motifs-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-4">
            <div className="nav-item mb-4">The signature</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight">
              Shop by <em className="italic">motif</em>.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-ink/80 text-base leading-relaxed self-end">
            Every piece is built around a single hand-embroidered figure. Choose the one your
            small human will not put down.
          </p>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-y-12 gap-x-4">
          {MOTIF_LIST.map((m) => <MotifCircle key={m.key} motifKey={m.key} label={m.label} />)}
        </div>
      </section>

      {/* Featured — Herbarium Album */}
      <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="featured-section">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end mb-16 md:mb-20">
          <div className="md:col-span-6">
            <div className="nav-item mb-3">The girls&rsquo; collection</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight">
              First of the <em className="italic">season</em>.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-8 text-ink/70 text-sm leading-relaxed">
            Six pieces catalogued like specimens in a field album. Each entry is dated, priced, and tagged with its motif. Open the entry to walk the piece.
          </p>
        </div>
        <CollectionHerbarium items={featured} />
        <div className="mt-16 flex justify-center">
          <Link to="/shop" className="crimson-link nav-item !text-[12px]" data-testid="featured-view-all">
            View the whole collection →
          </Link>
        </div>
      </section>

      {/* Editorial quote */}
      <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-2 flex md:justify-end">
            <Butterfly size={40} />
          </div>
          <blockquote className="md:col-span-8 serif text-3xl md:text-5xl leading-tight text-ink" data-testid="editorial-quote">
            &ldquo;Eleven hours. That&rsquo;s what one butterfly takes. We time it because it matters,
            not because we&rsquo;re in a hurry.&rdquo;
          </blockquote>
          <div className="md:col-span-2 nav-item text-ink/70">— Meghana &amp; Lallitha</div>
        </div>
      </section>

      <GrownupsBand />
      <TwinningBand />
    </div>
  );
};

export default Home;
